import { useNavigate } from 'react-router-dom';
import { MdOutlineClose } from 'react-icons/md';
import { Button, IconButton, Spinner, Tooltip } from '@ds';
import { EUROPE_DISABLED_MESSAGE, KIDNEY_DISABLED_MESSAGE } from '@lp/features/RoutePlanner/config';
import { apiBaseUrl } from '@/api/http';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { getIsEuropeanRegion } from '@/utils/helpers';
import { OrganType } from '@/api/apis/case';

const getSandboxUrl = (taskId: string) => `${apiBaseUrl}/mission-planner/sandbox/demoResult.html?demoId=${taskId}`;

type SuggestedRoutesHeaderProps = {
  isOpen: boolean;
  isDataLoading: boolean;
  isRoutesLoading: boolean;
  currentTaskId: string;
  hasBenchmark: boolean;
  hasRoutePlans: boolean;
  handleSuggestRoutesOpen: () => Promise<void>;
  onClose: () => void;
};

export const SuggestedRoutesHeader = ({
  isOpen,
  isDataLoading,
  isRoutesLoading,
  currentTaskId,
  hasBenchmark,
  hasRoutePlans,
  handleSuggestRoutesOpen,
  onClose,
}: SuggestedRoutesHeaderProps) => {
  const navigate = useNavigate();
  const { caseId, caseName, serviceRegionId, organType } = useBaseCaseDetails();

  const handleSwitchToMultiCase = () => {
    navigate(`/multi-case-suggest?caseId=${encodeURIComponent(caseId)}&caseName=${encodeURIComponent(caseName)}`);
  };

  const headerPadding = isOpen ? 'pl-4 pr-2 py-2' : 'p-4';
  const headerBg = isOpen ? 'bg-white' : 'bg-base-80';

  const isFilledStateText = hasRoutePlans || hasBenchmark;
  const btnText = isFilledStateText ? 'Suggested routes' : 'Suggest routes';

  const spinner = isRoutesLoading ? (
    <div className="p-1">
      <Spinner size="sm" color="white" />
    </div>
  ) : null;

  const isKidneyCase = organType === OrganType.Kidney;
  const isDisabledRegion = getIsEuropeanRegion(serviceRegionId);
  const isSuggestRoutesDisabled = isKidneyCase || isDisabledRegion;
  const disabledTooltipMessage = isKidneyCase ? KIDNEY_DISABLED_MESSAGE : EUROPE_DISABLED_MESSAGE;

  return (
    <div className={`${headerPadding} ${headerBg} rounded-t-2xl transition-background`}>
      {isOpen ? (
        <div className="flex gap-2 items-center">
          {isDataLoading ? (
            <span className="text-m-sb text-black">Suggesting in progress</span>
          ) : (
            <a
              href={getSandboxUrl(currentTaskId)}
              target="_blank"
              rel="noreferrer noopener"
              className="text-m-sb text-black cursor-default"
            >
              Suggested routes
            </a>
          )}
          <div className="ml-4">
            <Button size="sm" variant="outline" onPress={handleSwitchToMultiCase}>
              Switch to Multi-Case
            </Button>
          </div>
          <IconButton
            size="sm"
            radius="full"
            variant="action"
            onPress={onClose}
            startContent={<MdOutlineClose size={24} />}
          />
        </div>
      ) : (
        <Tooltip
          isDisabled={!isSuggestRoutesDisabled}
          className="text-sm text-base-5"
          placement="top"
          content={disabledTooltipMessage}
        >
          <div>
            <Button
              isDisabled={isSuggestRoutesDisabled}
              size="md"
              onPress={handleSuggestRoutesOpen}
              startContent={spinner}
            >
              {btnText}
            </Button>
          </div>
        </Tooltip>
      )}
    </div>
  );
};
